import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getMaxActors, getActorLabel } from "@gestionalo/enterprise-graph";
import type {
  ProductGraph,
  GraphState,
  ProductNode,
  PreconditionFieldDef,
  WizardFieldDef,
} from "@gestionalo/enterprise-graph";

type AnyFieldDef = PreconditionFieldDef | WizardFieldDef;

interface CompletedStepsSummaryProps {
  graph: ProductGraph;
  workflowState: GraphState;
  payload: Record<string, unknown>;
  /** When true, renders only the inner content without Card/CardHeader wrapper */
  headless?: boolean;
}

function getNodeFields(node: ProductNode): AnyFieldDef[] {
  if (node.type === "precondition" || node.type === "userForm") {
    const data = node.data as { fields?: AnyFieldDef[] };
    return data.fields ?? [];
  }
  return [];
}

function getNodeLabel(node: ProductNode): string | undefined {
  const data = node.data as { label?: string };
  return data.label;
}

const USER_VISIBLE_NODE_TYPES = new Set(["precondition", "userForm"]);

function formatFieldValue(
  field: AnyFieldDef,
  value: unknown,
): string {
  if (value === undefined || value === null || value === "") return "—";

  switch (field.type) {
    case "currency":
    case "number": {
      const num = Number(value);
      if (Number.isNaN(num)) return String(value);
      return num.toLocaleString("es-UY");
    }
    case "select": {
      const options = field.options;
      if (options) {
        const match = options.find((o) => o.value === String(value));
        if (match) return match.label;
      }
      return String(value);
    }
    case "checkbox":
      return value === true || value === "true" ? "Sí" : "No";
    case "file":
      return typeof value === "string" && value.length > 0 ? value : "Archivo adjunto";
    default:
      return String(value);
  }
}

export function CompletedStepsSummary({
  graph,
  workflowState,
  payload,
  headless = false,
}: CompletedStepsSummaryProps): JSX.Element | null {
  const { t } = useTranslation();

  const completedPath = workflowState.completedPath ?? [];
  if (completedPath.length === 0) return null;

  const stepsWithData: Array<{ node: ProductNode; fields: AnyFieldDef[] }> = [];

  for (const nodeId of completedPath) {
    const node = graph.nodes.find((n) => n.id === nodeId);
    if (!node || !USER_VISIBLE_NODE_TYPES.has(node.type)) continue;

    const fields = getNodeFields(node);
    if (fields.length === 0) continue;

    stepsWithData.push({ node, fields });
  }

  if (stepsWithData.length === 0) return null;

  const HIDDEN_FIELD_KEYS = new Set(['installments']);
  const HIDDEN_FIELD_TYPES = new Set(['file']);
  const showNodeHeadings = stepsWithData.length > 1;

  const content = (
    <div className="space-y-5">
      {stepsWithData.map(({ node, fields: rawFields }) => {
        const fields = rawFields.filter((f) => !HIDDEN_FIELD_KEYS.has(f.key) && !HIDDEN_FIELD_TYPES.has(f.type));
        const perActorFields = fields.filter((f) => 'perActor' in f && (f as { perActor?: boolean }).perActor);
        const sharedFields = fields.filter((f) => !('perActor' in f && (f as { perActor?: boolean }).perActor));
        // Use actual actor count from payload, falling back to max configured
        const nodePayloadRaw = payload[node.id];
        const nodePayloadObj = nodePayloadRaw && typeof nodePayloadRaw === 'object' && !Array.isArray(nodePayloadRaw)
          ? nodePayloadRaw as Record<string, unknown>
          : null;
        const actualActorCount = nodePayloadObj?.__actorCount != null
          ? Number(nodePayloadObj.__actorCount)
          : getMaxActors(graph);
        const actorCount = actualActorCount;
        const isMultiActor = perActorFields.length > 0 && actorCount > 1;

        const label = getActorLabel(graph);

        function renderFieldValue(field: AnyFieldDef, fieldKey: string): JSX.Element {
          const nodePayload = payload[node.id];
          const value =
            nodePayload && typeof nodePayload === "object" && !Array.isArray(nodePayload)
              ? (nodePayload as Record<string, unknown>)[fieldKey]
              : payload[fieldKey];
          const suffix = field.suffix;
          const formatted = formatFieldValue(field, value);
          return (
            <div key={fieldKey} className="grid grid-cols-[minmax(0,180px)_1fr] gap-3 py-1.5 first:pt-0">
              <dt className="text-sm text-muted-foreground">{field.label}</dt>
              <dd className="text-sm text-foreground">
                {formatted}
                {suffix && formatted !== "—" ? ` ${suffix}` : ""}
              </dd>
            </div>
          );
        }

        if (fields.length === 0) return null;

        return (
          <div key={node.id}>
            {showNodeHeadings && (
              <h4 className="mb-2 text-sm font-semibold text-foreground">
                {getNodeLabel(node) ?? node.id}
              </h4>
            )}

            {isMultiActor ? (
              <>
                <div className="space-y-3">
                  {Array.from({ length: actorCount }, (_, actorIdx) => (
                    <div key={actorIdx}>
                      <h5 className="mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {`${label} ${actorIdx + 1}`}
                      </h5>
                      <dl className="divide-y divide-border">
                        {perActorFields.map((field) => renderFieldValue(field, `${field.key}_${actorIdx}`))}
                      </dl>
                    </div>
                  ))}
                </div>
                {sharedFields.length > 0 && (
                  <dl className="divide-y divide-border mt-3 border-t pt-3">
                    {sharedFields.map((field) => renderFieldValue(field, field.key))}
                  </dl>
                )}
              </>
            ) : (
              <dl className="divide-y divide-border">
                {perActorFields.map((field) => renderFieldValue(field, `${field.key}_0`))}
                {sharedFields.map((field) => renderFieldValue(field, field.key))}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );

  if (headless) {
    return <CardContent className="pt-0">{content}</CardContent>;
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          {t("completedStepsSummary.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
